import React, { useState, useEffect } from 'react';
import { 
  Button, 
  Card, 
  CardBody, 
  CardHeader, 
  Col, 
  Container, 
  Modal, 
  ModalBody, 
  ModalFooter, 
  ModalHeader, 
  Row,
  Input,
  Label,
  FormFeedback,
  Form
} from 'reactstrap';
import BreadCrumb from '../../../Components/Common/BreadCrumb';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import DeleteModal from '../../../Components/Common/DeleteModal';

// Redux
import { useSelector, useDispatch } from 'react-redux';
import { getCommissions, addNewCommission, updateCommission, deleteCommission } from '../../../slices/thunks';
import { getLoggedinUser } from '../../../api';

const EcommerceCommissions = () => {
  const dispatch = useDispatch();
  const [modal_list, setModal_list] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [editingCommission, setEditingCommission] = useState(null);

  // Get user info
  const userProfile = getLoggedinUser();
  const userId = userProfile?.id || userProfile?.uid || userProfile?._id;

  // Get commissions from Redux state
  const commissionsRaw = useSelector((state) => state.Ecommerce?.commissions);
  const commissions = Array.isArray(commissionsRaw) ? commissionsRaw : [];

  // Status options
  const statusOptions = [
    { label: 'Active', value: 'Active' },
    { label: 'Inactive', value: 'Inactive' },
  ];

  // Fetch commissions on mount
  useEffect(() => {
    dispatch(getCommissions());
  }, [dispatch]);

  const tog_list = (commission = null) => {
    if (modal_list) {
      setModal_list(false);
      setEditingCommission(null);
      validation.resetForm();
    } else {
      setModal_list(true);
      if (commission) {
        setEditingCommission(commission);
        validation.setValues({
          name: commission.name || '',
          value: commission.value || '',
          description: commission.description || '',
          status: commission.status || 'Active',
        });
      } else {
        setEditingCommission(null);
      }
    }
  };

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: '',
      value: '',
      description: '',
      status: 'Active',
    },
    validationSchema: Yup.object({
      name: Yup.string().required('Commission name is required'),
      value: Yup.string().required('Commission value is required'),
      description: Yup.string(),
      status: Yup.string().required('Status is required'),
    }),
    onSubmit: async (values) => {
      try {
        if (editingCommission) {
          // Update commission
          const updateData = {
            id: editingCommission.id || editingCommission._id,
            name: values.name,
            value: values.value,
            description: values.description,
            status: values.status,
          };
          await dispatch(updateCommission(updateData)).unwrap();
        } else {
          // Add new commission
          const newCommission = {
            name: values.name,
            value: values.value,
            description: values.description,
            status: values.status,
            created_by: userId,
          };
          await dispatch(addNewCommission(newCommission)).unwrap();
        }
        tog_list(); // Close modal on success
        validation.resetForm();
      } catch (error) {
        // Error toast is handled by thunk
        console.error("Form submission error:", error);
      }
    },
  });

  const handleDelete = async (commissionId) => {
    try {
      await dispatch(deleteCommission(commissionId)).unwrap();
      setDeleteModal(false);
    } catch (error) {
      // Error toast is handled by thunk
      console.error("Delete error:", error);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch (e) {
      return dateString;
    }
  };

  document.title = "Commissions - Dashboard";

  return (
    <React.Fragment>
      <div className="page-content">
        <ToastContainer position="top-right" autoClose={3000} />
        <Container fluid>
          <BreadCrumb title="Commissions" pageTitle="Dashboard" />
          <Row>
            <Col lg={12}>
              <Card>
                <CardHeader>
                  <h4 className="card-title mb-0">Commissions</h4>
                </CardHeader>

                <CardBody>
                  <div className="listjs-table" id="commissionList">
                    <Row className="g-4 mb-3">
                      <Col className="col-sm-auto">
                        <div>
                          <Button 
                            color="success" 
                            className="add-btn me-1" 
                            onClick={() => tog_list()} 
                            id="create-btn"
                          >
                            <i className="ri-add-line align-bottom me-1"></i> Add Commission
                          </Button>
                        </div>
                      </Col>
                      <Col className="col-sm">
                        <div className="d-flex justify-content-sm-end">
                          <div className="search-box ms-2">
                            <input type="text" className="form-control search" placeholder="Search..." />
                            <i className="ri-search-line search-icon"></i>
                          </div>
                        </div>
                      </Col>
                    </Row>

                    <div className="table-responsive table-card mt-3 mb-1">
                      <table className="table align-middle table-nowrap" id="commissionTable">
                        <thead className="table-light">
                          <tr>
                            <th scope="col" style={{ width: "50px" }}>
                              <div className="form-check">
                                <input className="form-check-input" type="checkbox" id="checkAll" value="option" />
                              </div>
                            </th>
                            <th className="sort" data-sort="name">Name</th>
                            <th className="sort" data-sort="value">Value</th>
                            <th className="sort" data-sort="description">Description</th>
                            <th className="sort" data-sort="status">Status</th>
                            <th className="sort" data-sort="date">Created At</th>
                            <th className="sort" data-sort="action">Operations</th>
                          </tr>
                        </thead>
                        <tbody className="list form-check-all">
                          {commissions.length > 0 ? commissions.map((item, index) => (
                            <tr key={index}>
                              {/* Checkbox */}
                              <th scope="row">
                                <div className="form-check">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    name="checkAll"
                                    value={index + 1}
                                  />
                                </div>
                              </th>

                              {/* Name */}
                              <td>{item.name}</td>

                              {/* Value */}
                              <td>{item.value}</td>

                              {/* Description */}
                              <td>{item.description || '-'}</td>

                              {/* Status */}
                              <td className="status">
                                <span
                                  className={`badge ${
                                    item.status === "Active"
                                      ? "bg-success-subtle text-success"
                                      : "bg-danger-subtle text-danger"
                                  } text-uppercase`}
                                >
                                  {item.status}
                                </span>
                              </td>

                              {/* Created At */}
                              <td>{formatDate(item.created_at)}</td>

                              {/* Operations */}
                              <td>
                                <div className="d-flex gap-2">
                                  <div className="edit">
                                    <button
                                      className="btn btn-sm btn-success edit-item-btn"
                                      onClick={() => tog_list(item)}
                                    >
                                      <i className="ri-edit-line"></i>
                                    </button>
                                  </div>

                                  <div className="remove">
                                    <button
                                      className="btn btn-sm btn-danger remove-item-btn"
                                      onClick={() => {
                                        setDeleteModal(true);
                                        setEditingCommission(item);
                                      }}
                                    >
                                      <i className="ri-delete-bin-2-line"></i>
                                    </button>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )) : (
                            <tr>
                              <td colSpan="7" className="text-center py-5">
                                <p className="text-muted">No commissions found. Click "Add Commission" to create one.</p>
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Add/Edit Modal */}
          <Modal isOpen={modal_list} toggle={tog_list} centered>
            <ModalHeader toggle={tog_list}>
              {editingCommission ? 'Edit Commission' : 'Add Commission'}
            </ModalHeader>
            <Form onSubmit={validation.handleSubmit}>
              <ModalBody>
                <Row>
                  <Col lg={12}>
                    <div className="mb-3">
                      <Label htmlFor="name" className="form-label">
                        Name <span className="text-danger">*</span>
                      </Label>
                      <Input
                        type="text"
                        className="form-control"
                        id="name"
                        name="name"
                        placeholder="Enter commission name (e.g., Standard Commission, Premium Commission)"
                        value={validation.values.name}
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        invalid={validation.touched.name && validation.errors.name ? true : false}
                      />
                      {validation.touched.name && validation.errors.name ? (
                        <FormFeedback type="invalid">{validation.errors.name}</FormFeedback>
                      ) : null}
                    </div>
                  </Col>
                  <Col lg={12}>
                    <div className="mb-3">
                      <Label htmlFor="value" className="form-label">
                        Value <span className="text-danger">*</span>
                      </Label>
                      <Input
                        type="text"
                        className="form-control"
                        id="value"
                        name="value"
                        placeholder="Enter commission value (e.g., 10%, 5%, 15%)"
                        value={validation.values.value}
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        invalid={validation.touched.value && validation.errors.value ? true : false}
                      />
                      {validation.touched.value && validation.errors.value ? (
                        <FormFeedback type="invalid">{validation.errors.value}</FormFeedback>
                      ) : null}
                      <small className="text-muted">This value will appear in the product commission dropdown</small>
                    </div>
                  </Col>
                  <Col lg={12}>
                    <div className="mb-3">
                      <Label htmlFor="description" className="form-label">
                        Description
                      </Label>
                      <Input
                        type="textarea"
                        className="form-control"
                        id="description"
                        name="description"
                        rows="3"
                        placeholder="Enter commission description (optional)"
                        value={validation.values.description}
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                      />
                    </div>
                  </Col>
                  <Col lg={12}>
                    <div className="mb-3">
                      <Label htmlFor="status" className="form-label">
                        Status <span className="text-danger">*</span>
                      </Label>
                      <Input
                        type="select"
                        className="form-control"
                        id="status"
                        name="status"
                        value={validation.values.status}
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        invalid={validation.touched.status && validation.errors.status ? true : false}
                      >
                        {statusOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Input>
                      {validation.touched.status && validation.errors.status ? (
                        <FormFeedback type="invalid">{validation.errors.status}</FormFeedback>
                      ) : null}
                    </div>
                  </Col>
                </Row>
              </ModalBody>
              <ModalFooter>
                <Button color="light" onClick={tog_list}>
                  Cancel
                </Button>
                <Button color="primary" type="submit">
                  {editingCommission ? 'Update' : 'Add'}
                </Button>
              </ModalFooter>
            </Form>
          </Modal>

          {/* Delete Modal */}
          <DeleteModal
            show={deleteModal}
            onDeleteClick={() => {
              if (editingCommission) {
                handleDelete(editingCommission.id || editingCommission._id);
              }
            }}
            onCloseClick={() => {
              setDeleteModal(false);
              setEditingCommission(null);
            }}
          />
        </Container>
      </div>
    </React.Fragment>
  );
};

export default EcommerceCommissions;
